import { Button, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';
import { Table } from 'src/common/components/table/Table';
import type { ProviderCoverage } from '../types';
import HealthBadge from './HealthBadge';

type ProviderFeedStatusTableProps = {
  providers: ProviderCoverage[];
  onFixClick: (provider: ProviderCoverage) => void;
};

function formatLastSync(lastSync: string | null): string {
  if (!lastSync) return '—';

  const date = dayjs(lastSync);
  const now = dayjs();
  const hasTime = lastSync.includes('T');

  if (date.isSame(now, 'day')) return hasTime ? `Today, ${date.format('HH:mm')}` : 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day'))
    return hasTime ? `Yesterday, ${date.format('HH:mm')}` : 'Yesterday';

  const diffDays = now.diff(date, 'day');
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.format('D MMM YYYY');
}

const columns: DataTableColumn<ProviderCoverage>[] = [
  {
    accessor: 'provider',
    title: 'Provider',
    render: ({ provider }) => (
      <Text size="sm" fw={500}>
        {provider}
      </Text>
    ),
  },
  {
    accessor: 'health',
    title: 'Status',
    render: ({ health }) => <HealthBadge status={health} />,
  },
  {
    accessor: 'unmatchedCount',
    title: 'Unmatched',
    render: ({ unmatchedCount }) => (
      <Text size="sm" c={unmatchedCount > 0 ? 'brand.6' : 'dimmed'}>
        {unmatchedCount > 0
          ? `${unmatchedCount} ${unmatchedCount === 1 ? 'product' : 'products'}`
          : '—'}
      </Text>
    ),
  },
  {
    accessor: 'lastSync',
    title: 'Last sync',
    render: ({ lastSync }) => (
      <Text size="sm" c="dimmed">
        {formatLastSync(lastSync)}
      </Text>
    ),
  },
];

export default function ProviderFeedStatusTable({
  providers,
  onFixClick,
}: ProviderFeedStatusTableProps) {
  const tableColumns: DataTableColumn<ProviderCoverage>[] = [
    ...columns,
    {
      accessor: 'actions',
      title: '',
      textAlign: 'right',
      render: provider => (
        <Button
          variant="subtle"
          size="compact-sm"
          rightSection={<IconChevronRight size={14} />}
          onClick={() => onFixClick(provider)}
        >
          Fix
        </Button>
      ),
    },
  ];

  return (
    <Table<ProviderCoverage>
      title="Provider feed status"
      columns={tableColumns}
      data={providers}
      isLoading={false}
      onRowClick={onFixClick}
    />
  );
}
